import type {
    FaqItem,
    PricingPlan,
    ProcessStage,
    ServiceItem,
    TeamStage,
    Testimonial
} from '@/types'

// Copy for the studio's own pages. Case studies live in the CMS (projects
// collection); everything here is the agency's fixed positioning and offer.

export const clients: string[] = [
    "Stormborn",
    "Livit",
    "Apax",
    "SurgiPet",
    "Genan",
    "Altaura",
    "Memurai",
    "TopUpMeters",
    "Onion Security",
    "Taylor Stitch",
    "Doll Smash",
    "Tedif",
    "DJF Solicitors",
    "Consto Group",
    "Beesting",
    "LUX Rewards",
    "Three Over Six",
    "Tabat"
]

export const diagnosis = {
    intro: "Your expertise has grown, but your website still tells the old story. Prospective clients struggle to see what you offer, why it matters and whether you fit.",
    issues: [
        {
            title: "Hard to understand",
            text: "Your services make sense to your team, but visitors struggle to see which one they need."
        },
        {
            title: "Doesn't build trust",
            text: "Your expertise is there, but projects, client feedback and credentials are hard to find."
        },
        {
            title: "Messy structure",
            text: "Visitors jump between pages to piece together your services, pricing and how to start."
        },
        {
            title: "Too few enquiries",
            text: "The site attracts visitors, but too few suitable prospects take the next step with you."
        }
    ]
}

export const services: ServiceItem[] = [
    {
        slug: 'website-design',
        name: "Website design",
        situation: "Launching a new website",
        summary: "Strategy, UX/UI, content and Webflow brought together into one clear, effective website.",
        detail: "We plan your content, design your website in Figma and build it in Webflow, with a clear route from first visit to enquiry.",
        includes: [
            "Strategy and structure",
            "UX/UI design in Figma",
            "Webflow development",
            "CMS setup",
            "SEO-ready launch"
        ],
        image: '/images/services/website-design.avif',
        imageAlt: "KERN website layouts alongside typography, colours and reusable design components.",
        platform: "Webflow",
        timeline: "6 to 10 weeks",
        price: "From £2,500"
    },
    {
        slug: 'website-redesign',
        name: "Website redesign",
        situation: "Outgrowing your current website",
        summary: "Structure, messaging and design reworked to match where the business is now.",
        detail: "We keep what still works and rebuild what does not. URLs are mapped, valuable content is retained and redirects are tested before launch.",
        includes: [
            "Content and structure review",
            "Messaging and UX rework",
            "New visual design",
            "Webflow rebuild",
            "URL mapping and redirects"
        ],
        image: '/images/services/website-redesign.avif',
        imageAlt: "Northline Logistics website before and after redesign.",
        platform: "Webflow",
        timeline: "6 to 10 weeks",
        price: "From £5,000"
    },
    {
        slug: 'webflow-development',
        name: "Webflow development",
        situation: "Approved designs ready to build",
        summary: "Approved Figma files built into responsive Webflow websites your team can manage.",
        detail: "We build your approved designs in Webflow, with responsive layouts, reusable components and a CMS your team can manage. A fixed quote arrives within three working days.",
        includes: [
            "Clean, organised class structure",
            "Reusable components",
            "Custom CMS collections",
            "Performance checks",
            "Accessibility built in"
        ],
        image: '/images/services/webflow-development.avif',
        imageAlt: "NorthBridge Consulting website layouts prepared for development in Webflow.",
        platform: "Webflow",
        timeline: "3 to 5 weeks",
        price: "From £2,500"
    },
    {
        slug: 'wordpress-to-webflow',
        name: "WordPress to Webflow",
        situation: "Leaving WordPress behind",
        summary: "Content migrated to Webflow, with URLs, redirects and integrations planned before launch.",
        detail: "We move your content into a rebuilt Webflow platform and plan URLs, redirects and integrations before the switch, while your current website stays live.",
        includes: [
            "Content migration",
            "URL mapping",
            "Verified redirects",
            "Integrations carried across",
            "Rebuilt CMS structure"
        ],
        image: '/images/services/wordpress-to-webflow.avif',
        imageAlt: "NOVA Consulting website shown in WordPress and Webflow during migration.",
        platform: "Webflow"
    },
    {
        slug: 'ecommerce',
        name: "Ecommerce",
        situation: "Improving the buying journey",
        summary: "Webflow and Shopify stores that make products easier to find, understand and buy.",
        detail: "We design and build Webflow or Shopify stores that make products easier to find, understand and buy, especially on mobile, then tune them for 90 days after launch.",
        includes: [
            "Custom UX/UI design",
            "Collection and product pages",
            "Payments, shipping and tax",
            "Apps and integrations",
            "Launch and 90 days of tuning"
        ],
        image: '/images/services/ecommerce.avif',
        imageAlt: "MORA skincare store with a product collection and featured bestsellers.",
        platform: "Webflow or Shopify",
        timeline: "6 to 10 weeks",
        price: "Webflow from £5,000, Shopify from £8,000"
    },
    {
        slug: 'ai-agents',
        name: "AI website agents",
        situation: "Helping visitors take action",
        summary: "Agents that answer questions, qualify enquiries and guide visitors to the next step.",
        detail: "We build website agents that answer questions, collect project details and help visitors choose a service or book a call, trained on your business and connected to your workflow.",
        includes: [
            "Workflow design",
            "Knowledge and guardrails",
            "Conversation design",
            "System integrations",
            "Testing and launch"
        ],
        image: '/images/services/ai-agents.avif',
        imageAlt: "Atlas Property website with an AI assistant for commercial property enquiries.",
        platform: "Any website",
        price: "Quoted per agent"
    }
]

export const process: ProcessStage[] = [
    {
        index: '01',
        stage: "Foundation",
        title: "Strategy & structure",
        text: "We define your audience, key messages and sitemap before the design begins.",
        image: '/images/process/foundation.avif',
        imageAlt: "Website sitemap diagram with Home leading to Services, Case Studies, About, Insights and a conversion path."
    },
    {
        index: '02',
        stage: "Experience",
        title: "UX/UI design",
        text: "We design the pages in Figma, showing how the content works on desktop and mobile.",
        image: '/images/process/experience.avif',
        imageAlt: "NOVA webpage design presenting branding, web design and development services."
    },
    {
        index: '03',
        stage: "Build",
        title: "Webflow development",
        text: "We build reusable Webflow sections and an editable CMS, then test across devices.",
        image: '/images/process/build.avif',
        imageAlt: "Dashboard showing CMS collections and a platform overview with revenue, users and growth charts."
    },
    {
        index: '04',
        stage: "Growth",
        title: "Growth foundations",
        text: "We prepare search metadata, structured content and tracking for key enquiries.",
        image: '/images/process/growth.avif',
        imageAlt: "Graph showing website improvements over time."
    }
]

export const testimonials: Testimonial[] = [
    {
        quote: "Fit Design developed our designs in Webflow without compromising the details. The site is fast, responsive and easy for our team to manage.",
        name: "Charlotte Mindus",
        role: "Project Manager",
        company: "Livit Design",
        image: '/images/people/charlotte.avif',
        imageAlt: "Portrait of Charlotte Mindus.",
        stats: [
            { value: "15", label: "Pages developed" },
            { value: "11", label: "Reusable components" },
            { value: "−8 min", label: "Publishing time" }
        ]
    },
    {
        quote: "Tom, Katia and Tomasz made the whole process of redesigning my website effortless and genuinely fun to do.",
        name: "Mark Pagdin",
        role: "CEO",
        company: "Onion Security",
        image: '/images/people/mark.avif',
        imageAlt: "Portrait of Mark Pagdin in a dark blazer.",
        stats: [
            { value: "19", label: "Consultation bookings" },
            { value: "47", label: "Case study downloads" },
            { value: "+1.4k", label: "Organic visits" }
        ]
    },
    {
        quote: "Great experience with Fit Design. Their support, attention to detail and strategic approach made the eCommerce redesign a real success.",
        name: "Rachael Wilson",
        role: "Director",
        company: "Doll Smash Cosmetics",
        image: '/images/people/rachael.avif',
        imageAlt: "Portrait of Rachael Wilson resting her chin on her hand.",
        stats: [
            { value: "+42%", label: "Add-to-cart growth" },
            { value: "+£24.40", label: "Average order value" },
            { value: "2.8%", label: "Mobile checkout" }
        ]
    }
]

export const whyPoints = [
    {
        title: "Senior team, no outsourcing",
        text: "Speak directly with the team doing the work, from the first workshop to launch."
    },
    {
        title: "Fixed scope and pricing",
        text: "Know what is included, what it costs and what each side must provide before we begin."
    },
    {
        title: "One connected process",
        text: "Your message shapes the design, and the approved design guides the final build."
    },
    {
        title: "Built for long-term growth",
        text: "Update your CMS and add pages using reusable sections, with training at handover."
    },
    {
        title: "Proven Webflow delivery",
        text: "Explore our websites, client feedback and independent design recognition."
    }
]

export const pricing: PricingPlan[] = [
    {
        name: "New Website",
        price: "£2,500",
        description: "For a focused company website with clear service pages, a simple CMS and a clear path to enquiry.",
        note: "Includes CRM integrations",
        includes: [
            "Bespoke website design",
            "Webflow development",
            "Essential CMS setup",
            "SEO/AEO foundations",
            "Training & handover"
        ],
        cta: "Plan your website",
        service: 'website-design'
    },
    {
        name: "Growth Website",
        price: "£5,000",
        description: "For an established business ready to rethink its message, redesign key pages and rebuild in Webflow.",
        note: "8-week post-launch support",
        includes: [
            "Website strategy & planning",
            "UX/UI design & Webflow build",
            "CMS & CRM integrations",
            "SEO/AEO foundations",
            "Launch & team training"
        ],
        cta: "Plan your redesign",
        service: 'website-redesign'
    },
    {
        name: "Growth Partnership",
        price: "£750",
        period: "/mo",
        description: "For teams needing ongoing website updates, conversion improvements and search support after launch.",
        includes: [
            "Webflow development",
            "AI workflow support",
            "CRO improvements",
            "SEO/AEO optimisation",
            "Ongoing growth strategy"
        ],
        cta: "Explore partnership",
        service: 'growth-partnership'
    }
]

export const homeFaq: FaqItem[] = [
    {
        question: "Who is this for?",
        answer: "For founders and marketing teams whose business has outgrown its website and needs clear services, relevant proof and better enquiries."
    },
    {
        question: "What does the project price include?",
        answer: "Your scope covers strategy, design, Webflow development, testing and handover. Pages, content and integrations are set in your quote."
    },
    {
        question: "Will we own the website?",
        answer: "Yes. After full payment, the final work is yours. Hosting, fonts and other third-party services have their own terms."
    },
    {
        question: "Will a redesign affect our SEO?",
        answer: "We map URLs, retain valuable content and test redirects before launch. This reduces migration risk; rankings are not guaranteed."
    },
    {
        question: "Do we need copy and content ready first?",
        answer: "No. We refine existing copy or write new content. We agree who provides the words, images and approvals before work starts."
    },
    {
        question: "Do you only work in Webflow?",
        answer: "We build service and B2B websites in Webflow, and ecommerce stores in Shopify. We recommend the platform around your requirements."
    },
    {
        question: "How much should we budget?",
        answer: "New websites start from £2,500 and Growth Website projects from £5,000. Hosting and ongoing support are separate; your quote confirms the full scope."
    },
    {
        question: "What happens after launch?",
        answer: "We train your team and hand over the site. Launch support follows your package; ongoing work is agreed separately."
    },
    {
        question: "How long does a website project take?",
        answer: "Most design-and-build projects take 6 to 10 weeks; development from approved designs usually takes 3 to 5. Scope, content readiness and feedback affect the schedule."
    },
    {
        question: "Do you work with businesses outside London?",
        answer: "Yes. Our team is based in London and Gdańsk. We work with UK and international clients, with agreed feedback stages and a project schedule."
    }
]

export const servicesProcess = [
    {
        index: '01',
        verb: "Understand",
        title: "Define the need",
        text: "We review the business, the audience and the current website."
    },
    {
        index: '02',
        verb: "Shape",
        title: "Set the scope",
        text: "We choose the services, structure and functionality the project needs."
    },
    {
        index: '03',
        verb: "Create",
        title: "Bring it together",
        text: "Strategy, content, design and development run as one connected effort."
    },
    {
        index: '04',
        verb: "Improve",
        title: "Launch & evolve",
        text: "Testing, launch and handover. Ongoing support is agreed separately."
    }
]

export const servicesFaq: FaqItem[] = [
    {
        question: "Can we combine services?",
        answer: "Yes. Your scope includes only the services the project needs, whether that is one or several."
    },
    {
        question: "Can you work with our existing designs?",
        answer: "Yes. We can build approved designs, or step in earlier to refine UX, responsiveness and the design system."
    },
    {
        question: "Can you join an existing project?",
        answer: "Yes. We work alongside internal teams, outside designers and specialists."
    },
    {
        question: "Do you only work in Webflow?",
        answer: "No. Webflow is our main platform, Shopify is our preference for ecommerce, and we support WordPress where it is the right fit."
    },
    {
        question: "Where should we start?",
        answer: "Tell us what you want to improve and we'll recommend a starting point."
    }
]

export const contactFaq: FaqItem[] = [
    {
        question: "Can the website launch in phases?",
        answer: "Yes. We can prioritise the essential pages for launch, then add further content and features in planned phases."
    },
    {
        question: "Can you use our existing branding?",
        answer: "Yes. We can work within your current identity or refine it where needed to create a more consistent experience."
    },
    {
        question: "Can you integrate our CRM tools?",
        answer: "Yes. We can connect your website with platforms such as HubSpot, Salesforce, Zoho and other business tools."
    },
    {
        question: "How long does a typical project take?",
        answer: "Most projects take 6 to 10 weeks. The timeline depends on scope, content, integrations and how quickly feedback arrives."
    }
]

export const enquiryServices = [
    { value: 'free-homepage-preview', label: "Free homepage preview" },
    { value: 'website-design', label: "New website design" },
    { value: 'website-redesign', label: "Website redesign" },
    { value: 'webflow-development', label: "Webflow development" },
    { value: 'wordpress-to-webflow', label: "WordPress to Webflow" },
    { value: 'ecommerce', label: "Ecommerce" },
    { value: 'ai-agents', label: "AI website agents" },
    { value: 'growth-partnership', label: "Ongoing growth partnership" }
] as const

export const teamStages: TeamStage[] = [
    {
        index: '01',
        stage: "Direction",
        focus: "We define what makes the business valuable and set the direction for the website.",
        people: "Tom & Tomasz",
        role: "Strategy leads",
        portraits: [
            { src: '/images/team/01.webp', alt: "Illustrated portrait from the strategy team." },
            { src: '/images/team/03.webp', alt: "Illustrated portrait from the strategy team." }
        ]
    },
    {
        index: '02',
        stage: "Experience",
        focus: "We shape the messaging, the user journey and a design system that is easy to navigate.",
        people: "Matt & Philip",
        role: "Design team",
        portraits: [
            { src: '/images/team/05.webp', alt: "Illustrated portrait from the design team." },
            { src: '/images/team/06.webp', alt: "Illustrated portrait from the design team." }
        ]
    },
    {
        index: '03',
        stage: "Technology",
        focus: "We build a fast, scalable Webflow website your team can manage.",
        people: "Laslo & Mikolaj",
        role: "Development team",
        portraits: [
            { src: '/images/team/08.webp', alt: "Illustrated portrait from the development team." },
            { src: '/images/team/07.webp', alt: "Illustrated portrait from the development team." }
        ]
    },
    {
        index: '04',
        stage: "Visibility",
        focus: "We help the right people find you, through search, content and campaigns.",
        people: "Martyna & Peter",
        role: "Marketing team",
        portraits: [
            { src: '/images/team/02.webp', alt: "Illustrated portrait from the marketing team." },
            { src: '/images/team/09.webp', alt: "Illustrated portrait from the marketing team." }
        ]
    },
    {
        index: '05',
        stage: "Momentum",
        focus: "We launch, train your team and keep improving the website after it goes live.",
        people: "Hana & Kasia",
        role: "Growth & support",
        portraits: [
            { src: '/images/team/10.webp', alt: "Illustrated portrait from the growth and support team." },
            { src: '/images/team/04.webp', alt: "Illustrated portrait from the growth and support team." }
        ]
    }
]

export const ownershipValues = [
    {
        title: "Full ownership",
        text: "Once the project is fully paid, the finished website is yours, as set out in our agreement."
    },
    {
        title: "Simple content updates",
        text: "Your team edits everyday content without waiting for a developer."
    },
    {
        title: "A confident handover",
        text: "Training and documentation after launch, so nothing about the website is a mystery."
    },
    {
        title: "Support on your terms",
        text: "Keep us involved as your partner, or run everything in-house."
    },
    {
        title: "Built to evolve",
        text: "Reusable components make future pages and updates faster."
    }
]

export const principles = [
    {
        title: "Close to the work",
        text: "The strategists who plan your website stay involved through design, build and launch."
    },
    {
        title: "Working as one",
        text: "Strategy, content, design and Webflow progress together, so nothing is lost between stages."
    },
    {
        title: "Focused on outcomes",
        text: "Clearer user journeys, stronger credibility and better-quality enquiries."
    }
]

export const recognition = [
    { claim: "We know Webflow.", proof: "Certified Webflow Experts" },
    { claim: "We've done this before.", proof: "150+ websites delivered since 2012" },
    { claim: "Clients rate working with us.", proof: "Clutch Champions 2025" },
    { claim: "The design holds up.", proof: "5+ Awwwards Honourable Mentions, CSS Winner" },
    { claim: "We're close by.", proof: "Studios in London & Gdańsk" }
]

export const awards = [
    { src: '/images/awards/awwwards.webp', alt: "Awwwards Honourable Mention badge." },
    { src: '/images/awards/cssda.webp', alt: "CSS Design Awards badge." },
    { src: '/images/awards/indigo.webp', alt: "Indigo Design Award badge." },
    { src: '/images/awards/webflow-award.webp', alt: "Webflow Professional Partner badge." }
]

export const concepts = [
    { src: '/images/concepts/01.webp', alt: "Homepage concept with a pink cartoon cat above a colourful gradient." },
    { src: '/images/concepts/02.webp', alt: "Swirl homepage concept with a spiral of coloured dots." },
    { src: '/images/concepts/03.webp', alt: "Design is Everything homepage concept with a rainbow light trail." },
    { src: '/images/concepts/04.webp', alt: "Homepage concept with a dotted globe on a black background." },
    { src: '/images/concepts/05.webp', alt: "Homepage concept with a fan of visual feature cards." },
    { src: '/images/concepts/06.webp', alt: "Homepage concept with an abstract blue and purple light effect." },
    { src: '/images/concepts/07.webp', alt: "Homepage concept with dark dashboard cards for analytics and tasks." }
]
